import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getNewOperationDate } from 'utils/getNewOperationDate';
import { hasSocietyConstitutionOperation } from 'utils/filters';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import cleanText from 'utils/cleanText';

import { HistoryMovement, Movement, SelectorOption, Transaction } from 'types';

import { cleanValue } from 'constants/formats';
import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import transactionTypes from 'constants/transactionTypes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { addOperation } from 'modules/transactions/redux/operationActions';
import { useGetHoldingClassOptionsForOperationModal } from 'modules/transactions/hooks/useGetHoldingClassOptionsForOperationModal';
import { useGetAvailableRangeByHoldingClass } from 'modules/_shared/hooks/useGetAvailableRangeByHoldingClass';
import { createTransaction } from 'modules/transactions/utils/createTransaction';
import movementTypes from 'modules/transactions/constants/movementTypes';

import { createMovement } from 'modules/transactions/utils/createMovement';
import { ConstitutionModalContextType } from '../types/ConstitutionModalContextType';
import { constitutionModalInitValue } from '../constants/constitutionModalInitValue';
import constitutionModalSteps, {
  ConstitutionModalStep,
} from '../constants/constitutionModalSteps';
import {
  ConstitutionExcelPreview,
  ConstitutionExcelRow,
} from '../types/ConstitutionExcel';
import { parseConstitutionExcel } from '../utils/parseConstitutionExcel';
import { validateConstitutionExcelRowsWithCategories } from '../utils/validateConstitutionExcelRows';
import { mapConstitutionExcelToPreview } from '../utils/mapConstitutionExcelToPreview';
import { mapConstitutionPreviewToOperation } from '../utils/mapConstitutionPreviewToOperation';
import { resolveOrCreatePartnersFromExcel } from '../utils/resolveOrCreatePartnersFromExcel';
import { downloadConstitutionExcelTemplate } from '../utils/downloadConstitutionExcelTemplate';
import {
  ConstitutionExistingPartner,
  fetchExistingPartnersForConstitution,
} from '../utils/fetchExistingPartnersForConstitution';

const ConstitutionModalContext = createContext<ConstitutionModalContextType>(
  constitutionModalInitValue
);

export const ConstitutionModalProvider: FC<{
  children: ReactNode;
  /** Usado por App.js para el tamaño del diálogo Bootstrap */
  size?: string;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const holdingClasses = useSelector((state: State) => state.holdingClasses);
  const partnerCategories = useSelector(
    (state: State) => state.partnerCategories
  );

  const [date, setDate] = useState<string>('');
  const [step, setStep] = useState<ConstitutionModalStep>(
    constitutionModalSteps.SCRIPTURE
  );
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [excelFile, setExcelFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [totalSharesToAdd, setTotalSharesToAdd] = useState<number>(0);
  const [nominalValue, setNominalValue] = useState<number>(0);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [sharesToAdd, setSharesToAdd] = useState<number>(0);
  const [remainingShares, setRemainingShares] = useState<number>(0);
  const [hasPremium, setHasPremium] = useState<boolean>(false);
  const [premium, setPremium] = useState<number>(0);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [excelPreview, setExcelPreview] =
    useState<ConstitutionExcelPreview | null>(null);
  const [excelRows, setExcelRows] = useState<ConstitutionExcelRow[]>([]);
  const [newPartnerCategories, setNewPartnerCategories] = useState<string[]>(
    []
  );
  const [existingPartnersForExcel, setExistingPartnersForExcel] = useState<
    ConstitutionExistingPartner[]
  >([]);
  const [isExcelMode, setIsExcelMode] = useState<boolean>(false);
  const [isValidateRangeEnabled, setIsValidateRangeEnabled] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isParsingExcel, setIsParsingExcel] = useState<boolean>(false);

  const { lastOperationDate } = useGetLastOperationDate(
    actualSociety?._id
  ).data;

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptionsForOperationModal({
      societyId: actualSociety?.['_id'],
      isSingleSelect: true,
    });

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptionsForOperationModal({
      societyId: actualSociety?._id,
    });

  const {
    data: availableRangeData,
    actions: availableRangeActions,
    isLoading: isLoadingRange,
  } = useGetAvailableRangeByHoldingClass();

  const resetExcelState = () => {
    setExcelPreview(null);
    setExcelRows([]);
    setNewPartnerCategories([]);
    setExistingPartnersForExcel([]);
    setIsExcelMode(false);
    setHistoryMovements([]);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeComments = (comment: string) => {
    setComments(comment);
  };

  const handleChangeFile = (selectedFile: File) => {
    setFile(selectedFile);
  };

  const handleDownloadExcelTemplate = () => {
    downloadConstitutionExcelTemplate();
  };

  const handleChangeExcelFile = async (selectedFile: File) => {
    if (!selectedFile || !actualSociety?._id) return;

    setExcelFile(selectedFile);
    setIsParsingExcel(true);
    resetExcelState();

    try {
      const [parseResult, fetchedExistingPartners] = await Promise.all([
        parseConstitutionExcel(selectedFile),
        fetchExistingPartnersForConstitution(actualSociety._id),
      ]);

      if (!parseResult.success) {
        errorSwal(t('ConstitutionExcelErrorSheetNotFound'));
        setExcelFile(undefined);
        return;
      }

      setExistingPartnersForExcel(fetchedExistingPartners);

      const validationResult = validateConstitutionExcelRowsWithCategories({
        rawRows: parseResult.rows,
        holdingClasses: holdingClasses || [],
        existingPartners: fetchedExistingPartners,
        isSocietyConstituted: hasSocietyConstitutionOperation(actualSociety),
        existingPartnerCategories: partnerCategories || [],
      });

      const preview = mapConstitutionExcelToPreview({
        rows: validationResult.rows,
        holdingClasses: holdingClasses || [],
        errors: validationResult.errors,
      });

      setExcelRows(validationResult.rows);
      setExcelPreview(preview);
      setNewPartnerCategories(validationResult.newPartnerCategories);
      setTotalSharesToAdd(validationResult.totalShares);
      setRemainingShares(validationResult.totalShares);
      setHistoryMovements(preview.historyMovements);
      setIsExcelMode(true);
      setStep(constitutionModalSteps.PREVIEW);
    } catch {
      errorSwal(t('ConstitutionExcelErrorParsingFile'));
      setExcelFile(undefined);
    } finally {
      setIsParsingExcel(false);
    }
  };

  const handleChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const handleChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const handleChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangeTotalSharesToAdd = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (isExcelMode) return;

    const cleanedValue = cleanValue(event.target.value);
    setTotalSharesToAdd(cleanedValue);
    setRemainingShares(cleanedValue);
  };

  const handleChangeNominalValue = (event: ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = cleanValue(event.target.value);
    setNominalValue(cleanedValue);
  };

  const handleChangePartner = (selectedOption: SelectorOption) => {
    setSelectedPartner(selectedOption?.value || '');
    partnerOptionsActions.handleChangeSelectedPartnerFirstOption(
      selectedOption
    );
  };

  const handleChangeSharesToAdd = (event: ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = cleanValue(event.target.value);
    setSharesToAdd(cleanedValue);
    setIsValidateRangeEnabled(true);
  };

  const handleChangeHasPremium = () => {
    setHasPremium((prev) => !prev);
    setPremium(0);
  };

  const handleChangePremium = (event: ChangeEvent<HTMLInputElement>) => {
    const cleanedValue = cleanValue(event.target.value);
    setPremium(cleanedValue);
  };

  const handleSaveMovement = () => {
    const range = {
      ...availableRangeData.availableRange,
      shareClass: holdingClassOptionsData.selectedHoldingClass,
      sharePremium: premium,
      partner: selectedPartner,
    };
    const movement = createMovement({
      range,
      movementType: movementTypes.INCREMENT,
    });
    const transaction = createTransaction({
      range,
      transactionType: transactionTypes.CONSTITUTION,
    });

    setMovements([...movements, movement]);
    setTransactions([...transactions, transaction]);

    const historyMovement: HistoryMovement = {
      from: range.from,
      to: range.to,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label || '',
      partnerNameSeller: partnerOptionsData.selectedPartnerFirstOption?.label
        .split('|')[1]
        .trim(),
    };

    setHistoryMovements([...historyMovements, historyMovement]);

    availableRangeActions.handleAddRange({
      from: range.from,
      to: range.to,
      shareClass: holdingClassOptionsData.selectedHoldingClass,
    });

    setSharesToAdd(0);
    setRemainingShares((prev) => prev - sharesToAdd);
  };

  const handleChangeHoldingClass = (selectedOption: SelectorOption) => {
    holdingClassOptionsActions.handleClassOptionChange(selectedOption);
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: selectedOption?.value || '',
      sharesCount: sharesToAdd,
    });
  };

  const handleValidateRange = () => {
    setIsValidateRangeEnabled(false);
    availableRangeActions.handleValidateRange({
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
      sharesCount: sharesToAdd,
    });
  };

  const submitConstitutionOperation = async ({
    operationMovements,
    operationTransactions,
  }: {
    operationMovements: Movement[];
    operationTransactions: Transaction[];
  }) => {
    try {
      setIsLoading(true);

      let uploadedDocument = null;

      if (file) {
        const newDocument = {
          file,
          date: new Date(date),
          size: file.size,
          name: operationTypes.CONTRIBUTION_FILENAME,
          author: user['_id'],
          society: actualSociety['_id'],
          fileType: fileTypes[file.type as keyof typeof fileTypes],
          category: documentTypes.SCRIPTURES,
          description: t('DocumentLinkedToOperation'),
        };
        uploadedDocument = await store.dispatch(addDocument(newDocument));
      }

      const newOperationDate = getNewOperationDate(lastOperationDate, date);

      const operationData = {
        movements: operationMovements,
        transactions: operationTransactions,
        date: newOperationDate,
        nominalValue,
        society: actualSociety['_id'],
        user: user['_id'],
        documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
        comments: cleanText(comments),
        notarialRegistration: {
          notaryName,
          notaryCity,
          protocolNr: notaryProtocol,
        },
        operationType: operationTypes.CONSTITUTION,
      };

      await store.dispatch(addOperation(operationData));

      successSwal(t('ConstitutionAddedSuccessfully'));
    } catch {
      errorSwal(t('ErrorAddingConstitution'));
    } finally {
      setIsLoading(false);
      handleCloseModal();
    }
  };

  const handleSaveConstitution = async () => {
    if (step === constitutionModalSteps.SCRIPTURE) {
      if (isExcelMode && excelPreview) {
        setStep(constitutionModalSteps.PREVIEW);
      } else {
        setStep(constitutionModalSteps.MOVEMENTS);
      }
      return;
    }

    await submitConstitutionOperation({
      operationMovements: movements,
      operationTransactions: transactions,
    });
  };

  const handleBackToScripture = () => {
    setStep(constitutionModalSteps.SCRIPTURE);
  };

  const handleExecuteFromExcel = async () => {
    if (!excelPreview || !excelPreview.items.length) {
      errorSwal(t('ConstitutionExcelErrorNoRows'));
      return;
    }

    if (excelPreview.errors.length > 0) {
      errorSwal(t('ConstitutionCorrectExcelToContinue'));
      return;
    }

    if (!date || date < lastOperationDate) {
      errorSwal(t('InvalidOperationDate'));
      return;
    }

    if (!nominalValue || nominalValue <= 0) {
      errorSwal(t('ConstitutionExcelErrorMissingNominalValue'));
      return;
    }

    try {
      setIsLoading(true);

      const newOperationDate = getNewOperationDate(lastOperationDate, date);
      const partnerIdByCif = await resolveOrCreatePartnersFromExcel({
        rows: excelRows,
        preview: excelPreview,
        society: actualSociety,
        user,
        operationDate: newOperationDate,
        holdingClasses: holdingClasses || [],
        partnerCategories: partnerCategories || [],
        existingPartners: existingPartnersForExcel,
        newPartnerCategories,
      });

      const {
        movements: operationMovements,
        transactions: operationTransactions,
      } = mapConstitutionPreviewToOperation({
        preview: excelPreview,
        partnerIdByCif,
      });

      await submitConstitutionOperation({
        operationMovements,
        operationTransactions,
      });
    } catch {
      errorSwal(t('ErrorAddingConstitution'));
      setIsLoading(false);
    }
  };

  return (
    <ConstitutionModalContext.Provider
      value={{
        data: {
          date,
          comments,
          file,
          excelFile,
          notaryName,
          notaryProtocol,
          notaryCity,
          step,
          lastOperationDate,
          totalSharesToAdd,
          nominalValue,
          selectedPartner,
          partnerOptions: partnerOptionsData.partnerFirstOptions,
          sharesToAdd,
          remainingShares,
          hasPremium,
          premium,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          defaultHoldingClassOption:
            holdingClassOptionsData.defaultHoldingClassOption,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          availableRange: availableRangeData.availableRange,
          historyMovements,
          excelPreview,
          excelRows,
          newPartnerCategories,
          isExcelMode,
          isValidateRangeEnabled,
          isLoadingRange,
          isLoading,
          isParsingExcel,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeComments,
          handleChangeFile,
          handleChangeExcelFile,
          handleDownloadExcelTemplate,
          handleChangeNotaryName,
          handleChangeNotaryProtocol,
          handleChangeNotaryCity,
          handleChangeTotalSharesToAdd,
          handleChangeNominalValue,
          handleChangePartner,
          handleSearchPartnerByTerm: partnerOptionsActions.fetchPartnersByTerm,
          handleChangeSharesToAdd,
          handleChangeHasPremium,
          handleChangePremium,
          handleChangeHoldingClass,
          handleValidateRange,
          handleSaveMovement,
          handleSaveConstitution,
          handleBackToScripture,
          handleExecuteFromExcel,
        },
      }}
    >
      {children}
    </ConstitutionModalContext.Provider>
  );
};

export const useConstitutionModal = () => {
  const context = useContext(ConstitutionModalContext);
  if (!context) {
    throw new Error(
      'useConstitutionModal must be used within a ConstitutionModalProvider'
    );
  }
  return context;
};
